import { Event, EventEmitter, Element, Prop, Component, Host, h, State } from '@stencil/core';

export interface AhaModalHeader extends HTMLAhaModalHeaderElement {}

@Component({
  tag: 'aha-modal-header',
  styleUrl: 'aha-modal-header.css',
  shadow: true,
})
export class AhaModalHeader {
  /**
   * The primary header of the modal.
   */
  @Prop({ reflect: true }) modalTitle!: string;

  /**
   * The optional, secondary subheader of the modal;
   */
  @Prop({ reflect: true }) modalSubtitle!: string;

  /**
   * The heading given to the default `aha-settings-popover` component in the header.
   */
  @Prop({ reflect: true }) helpHeading!: string;
  /**
   * Whether the header is a warning header.
   */
  @Prop({ reflect: true }) warning?: boolean;

  /** Wether or not dragging on the `<aha-modal-header>` will cause the parent `<aha-modal>` to be dragged. */
  @Prop({ reflect: true, attribute: 'prevent-drag' }) preventDrag?: boolean;

  @Element() host!: HTMLAhaModalHeaderElement;

  @State() simpleLayout: boolean = false;

  /**
   * Event fired when the user activates the close icon.
   */
  @Event({ eventName: 'aha-modal-header:close', composed: true, cancelable: true, bubbles: true }) //eslint-disable-line @stencil/decorators-style
  modalClose!: EventEmitter<any>;

  private handleClose = (event: Event) => {
    if (event instanceof MouseEvent) {
      this.modalClose.emit(this.host);
    } else if (event instanceof KeyboardEvent) {
      switch (event.type) {
        case 'keyup': {
          if (event.key === ' ') {
            event.preventDefault();
            this.modalClose.emit(this.host);
          }
        }
        case 'keydown': {
          if (event.key === ' ') {
            event.preventDefault();
          } else if (event.key === 'Enter') {
            event.preventDefault();
            this.modalClose.emit(this.host);
          }
        }
      }
    }
  };

  connectedCallback() {
    this.handleClose = this.handleClose.bind(this);
    if (!this.host.slot && this.host.parentElement?.tagName === 'AHA-MODAL') {
      this.host.slot = 'modal-header';
    }
  }

  handleContentSlotChange = (e: Event) => {
    const target = e.target as HTMLSlotElement;
    if (target) {
      this.simpleLayout = target.assignedNodes().length > 0;
    }
  };

  render() {
    const hasSubtitle = Boolean(
      this.modalSubtitle || this.host.querySelector('[slot="subtitle"]')?.innerHTML,
    );

    const hasTitle = Boolean(
      this.modalTitle || this.host.querySelector('[slot="title"]')?.innerHTML,
    );

    return (
      <Host class={hasTitle && !hasSubtitle ? 'aha-modal-header--small' : ''}>
        {this.warning && <aha-icon icon="fa-regular fa-triangle-exclamation"></aha-icon>}
        <slot
          name="contents"
          onSlotchange={this.handleContentSlotChange}
          aria-hidden={String(!this.simpleLayout)}
        ></slot>
        <div id="wrapper" aria-hidden={String(this.simpleLayout)}>
          <div class={`header__labels ${hasSubtitle ? '' : 'header__labels--small'}`}>
            <div class="header__title">
              <slot name="title">{this.modalTitle}</slot>
              <slot></slot>
            </div>
            {hasSubtitle && (
              <div class="header__subtitle">
                <slot name="subtitle">{this.modalSubtitle}</slot>
              </div>
            )}
          </div>
          <div class={`header__actions ${hasSubtitle ? '' : 'header__actions--small'}`}>
            <slot name="header-actions">
              <div class="default-header__actions">
                <slot name="secondary-actions">
                  <aha-settings-popover
                    style={{ display: Boolean(this.helpHeading) ? 'inherit' : 'none' }}
                    heading={this.helpHeading}
                  >
                    <slot name="help-content"></slot>
                  </aha-settings-popover>
                </slot>
                <div
                  class="header__close"
                  onClick={this.handleClose}
                  onKeyDown={this.handleClose}
                  onKeyUp={this.handleClose}
                  role="button"
                  tabindex="0"
                >
                  <fa-icon class="fa-regular fa-close"></fa-icon>
                </div>
              </div>
            </slot>
          </div>
        </div>
      </Host>
    );
  }
}
